import React, { useState, useMemo, useRef, useEffect } from "react";
import styles from "./AffiliatesAndSalesRepsDropdown.module.css";

export interface AffiliateItem {
  id: string;
  type: "affiliate";
  firstName: string;
  lastName: string;
  emailId: string;
  aId: string;
  referralCode?: string;
  aType?: string;
}

export interface SalesRepItem {
  id: string;
  type: "sales_rep";
  firstName: string;
  lastName: string;
  emailId: string;
  sr_id: string;
}

export type CombinedItem = AffiliateItem | SalesRepItem;

interface AffiliatesAndSalesRepsDropdownProps {
  affiliates: any[];
  salesReps: any[];
  selectedItem: CombinedItem | null;
  onSelect: (item: CombinedItem | null) => void;
  isDarkMode?: boolean;
  placeholder?: string;
}

const AffiliatesAndSalesRepsDropdown: React.FC<AffiliatesAndSalesRepsDropdownProps> = ({
  affiliates,
  salesReps,
  selectedItem,
  onSelect,
  isDarkMode = false,
  placeholder = "Select Affiliate or Sales Rep",
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const dropdownRef = useRef<HTMLDivElement>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);

  // Combine and normalize data
  const combinedData = useMemo(() => {
    const items: CombinedItem[] = [];

    // Add affiliates
    (affiliates || []).forEach((affiliate: any) => {
      if (affiliate?.aId) {
        items.push({
          id: `aff-${affiliate.aId}`,
          type: "affiliate",
          firstName: affiliate.firstName || affiliate.a_first_name || "",
          lastName: affiliate.lastName || affiliate.a_last_name || "",
          emailId: affiliate.emailId || affiliate.a_email_id || "",
          aId: affiliate.aId,
          referralCode: affiliate.referralCode || affiliate.a_referral_code || "",
          aType: affiliate.aType || affiliate.a_type || "Direct",
        });
      }
    });

    // Add sales reps
    (salesReps || []).forEach((rep: any) => {
      if (rep?.sr_id) {
        items.push({
          id: `rep-${rep.sr_id}`,
          type: "sales_rep",
          firstName: rep.sr_first_name || "",
          lastName: rep.sr_last_name || "",
          emailId: rep.sr_email || "",
          sr_id: rep.sr_id,
        });
      }
    });

    return items;
  }, [affiliates, salesReps]);

  // Filter data based on search
  const filteredData = useMemo(() => {
    if (!searchTerm.trim()) return combinedData;

    const q = searchTerm.toLowerCase();
    return combinedData.filter((item) => {
      const fullName = `${item.firstName} ${item.lastName}`.toLowerCase();
      const email = item.emailId.toLowerCase();
      const idValue = item.type === "affiliate" 
        ? ((item as AffiliateItem).referralCode || "").toLowerCase()
        : "";

      return fullName.includes(q) || email.includes(q) || idValue.includes(q);
    });
  }, [combinedData, searchTerm]);

  // Group filtered data by type
  const groupedData = useMemo(() => {
    const affiliatesList = filteredData.filter((item) => item.type === "affiliate");
    const salesRepsList = filteredData.filter((item) => item.type === "sales_rep");
    return { affiliatesList, salesRepsList };
  }, [filteredData]);

  // Handle outside click
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener("mousedown", handleClickOutside);
      return () => document.removeEventListener("mousedown", handleClickOutside);
    }
  }, [isOpen]);

  // Focus search when opened
  useEffect(() => {
    if (isOpen && searchInputRef.current) {
      searchInputRef.current.focus();
    }
  }, [isOpen]);

  const getDisplayLabel = (item: CombinedItem | null): string => {
    if (!item) return placeholder;
    const fullName = `${item.firstName} ${item.lastName}`.trim();
    if (fullName) return fullName;
    if (item.emailId) return item.emailId;
    return item.type === "affiliate" ? "Affiliate" : "Sales Rep";
  };

  const getTypeLabel = (item: CombinedItem): string => {
    if (item.type === "affiliate") {
      return (item as AffiliateItem).aType || "Affiliate";
    }
    return "Sales Rep";
  };

  const getTypeColor = (item: CombinedItem): string => {
    if (item.type === "affiliate") {
      const aType = (item as AffiliateItem).aType;
      if (aType === "Direct") return "#eef2ff";
      if (aType === "Indirect") return "#ede9fe";
      if (aType === "Both") return "#fef3c7";
      return "#eef2ff";
    }
    return isDarkMode ? "#2d3a4e" : "#dbeafe";
  };

  const getTypeTextColor = (item: CombinedItem): string => {
    if (item.type === "affiliate") {
      const aType = (item as AffiliateItem).aType;
      if (aType === "Direct") return "#1e40af";
      if (aType === "Indirect") return "#6b21a8";
      if (aType === "Both") return "#92400e";
      return "#1e40af";
    }
    return isDarkMode ? "#93c5fd" : "#1e40af";
  };

  return (
    <div 
      ref={dropdownRef}
      className={`${styles.container} ${isDarkMode ? styles.dark : ""}`}
    >
      {/* Display Selected Value */}
      <button
        className={styles.trigger}
        onClick={() => setIsOpen(!isOpen)}
        type="button"
      >
        <div className={styles.triggerContent}>
          <span className={styles.triggerLabel}>{getDisplayLabel(selectedItem)}</span>
          {selectedItem && (
            <span
              className={styles.typeBadge}
              style={{
                backgroundColor: getTypeColor(selectedItem),
                color: getTypeTextColor(selectedItem),
              }}
            >
              {getTypeLabel(selectedItem)}
            </span>
          )}
        </div>
        <svg
          className={`${styles.chevron} ${isOpen ? styles.open : ""}`}
          xmlns="http://www.w3.org/2000/svg"
          width="18"
          height="18"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <polyline points="6 9 12 15 18 9"></polyline>
        </svg>
      </button>

      {/* Dropdown Menu */}
      {isOpen && (
        <div className={styles.menu}>
          {/* Search Input */}
          <div className={styles.searchContainer}>
            <input
              ref={searchInputRef}
              type="text"
              placeholder="Search by name, email, or ID..."
              className={styles.searchInput}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
            <svg
              className={styles.searchIcon}
              xmlns="http://www.w3.org/2000/svg"
              width="16"
              height="16"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <circle cx="11" cy="11" r="8"></circle>
              <path d="m21 21-4.35-4.35"></path>
            </svg>
          </div>

          {/* "None" Option */}
          <div className={styles.section}>
            <button
              className={`${styles.item} ${
                selectedItem === null ? styles.selected : ""
              }`}
              onClick={() => {
                onSelect(null);
                setIsOpen(false);
                setSearchTerm("");
              }}
              type="button"
            >
              <div className={styles.itemContent}>
                <span className={styles.itemLabel}>None</span>
              </div>
            </button>
          </div>

          {/* Affiliates Section */}
          {groupedData.affiliatesList.length > 0 && (
            <div className={styles.section}>
              <div className={styles.sectionHeader}>Affiliates</div>
              {groupedData.affiliatesList.map((item) => (
                <button
                  key={item.id}
                  className={`${styles.item} ${
                    selectedItem?.id === item.id ? styles.selected : ""
                  }`}
                  onClick={() => {
                    onSelect(item);
                    setIsOpen(false);
                    setSearchTerm("");
                  }}
                  type="button"
                >
                  <div className={styles.itemContent}>
                    <div className={styles.itemHeader}>
                      <span className={styles.itemLabel}>
                        {`${item.firstName} ${item.lastName}`.trim() || item.emailId}
                      </span>
                      <span
                        className={styles.itemBadge}
                        style={{
                          backgroundColor: getTypeColor(item),
                          color: getTypeTextColor(item),
                        }}
                      >
                        {getTypeLabel(item)}
                      </span>
                    </div>
                    <div className={styles.itemDetails}>
                      <span className={styles.itemEmail}>{item.emailId}</span>
                      {(item as AffiliateItem).referralCode && (
                        <span className={styles.itemId}>
                          ID: {(item as AffiliateItem).referralCode}
                        </span>
                      )}
                    </div>
                  </div>
                </button>
              ))}
            </div>
          )}

          {/* Sales Reps Section */}
          {groupedData.salesRepsList.length > 0 && (
            <div className={styles.section}>
              <div className={styles.sectionHeader}>Sales Reps</div>
              {groupedData.salesRepsList.map((item) => (
                <button
                  key={item.id}
                  className={`${styles.item} ${
                    selectedItem?.id === item.id ? styles.selected : ""
                  }`}
                  onClick={() => {
                    onSelect(item);
                    setIsOpen(false);
                    setSearchTerm("");
                  }}
                  type="button"
                >
                  <div className={styles.itemContent}>
                    <div className={styles.itemHeader}>
                      <span className={styles.itemLabel}>
                        {`${item.firstName} ${item.lastName}`.trim() || item.emailId}
                      </span>
                      <span
                        className={styles.itemBadge}
                        style={{
                          backgroundColor: isDarkMode ? "#2d3a4e" : "#dbeafe",
                          color: isDarkMode ? "#93c5fd" : "#1e40af",
                        }}
                      >
                        Sales Rep
                      </span>
                    </div>
                    <div className={styles.itemDetails}>
                      <span className={styles.itemEmail}>{item.emailId}</span>
                      <span className={styles.itemId}>
                        ID: {(item as SalesRepItem).sr_id.substring(0, 8)}
                      </span>
                    </div>
                  </div>
                </button>
              ))}
            </div>
          )}

          {/* No Results */}
          {groupedData.affiliatesList.length === 0 &&
            groupedData.salesRepsList.length === 0 &&
            searchTerm.trim() && (
              <div className={styles.noResults}>
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <circle cx="11" cy="11" r="8"></circle>
                  <path d="m21 21-4.35-4.35"></path>
                </svg>
                <span>No results found for "{searchTerm}"</span>
              </div>
            )}
        </div>
      )}
    </div>
  );
};

export default AffiliatesAndSalesRepsDropdown;
